import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import TreatmentGallery from '../components/TreatmentGallery';
import TreatmentFAQ from '../components/TreatmentFAQ';
import { imageUrl } from '../lib/images';

// Fallback treatment detail data in case CMS is loading or matching fallback
const FALLBACK_TREATMENTS = {
  'fysiurgisk-massage': {
    title: 'Fysiurgisk massage & Sportsmassage',
    therapist: 'Tine (RAB-godkendt massør · Falck & AP Pension)',
    price_info: 'Fra 300 kr. (600 kr./time) · RAB-godkendt (Falck & AP Pension dækning)',
    gallery_slug: 'fysiurgisk-massage',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/fysiurgisk-massage-rygbehandling.jpg'],
    short_description: 'Dybdegående fysiurgisk massage og sportsmassage. Tine er RAB-godkendt – det eneste officielle kvalitetsstempel for massører i Danmark og kravet for sundhedsforsikringsdækning (Falck & AP Pension).',
    content: `
      <p>Fysiurgisk massage er en dybdegående massageform, der tager udgangspunkt i kroppens anatomi og fysiologi. Den fokuserer på at løsne spændinger i muskler og sener samt forbedre blodcirkulationen. Det giver ofte en mærkbar smertelindring i leddene, da muskler og sener slipper deres spænding og dermed ikke længere trækker skævt i leddene.</p>
      <p><strong>Målrettet sportsmassage:</strong> Tine tilbyder desuden professionel <strong>sportsmassage</strong> til både aktive sportsudøvere og motionister. Sportsmassage er ideel til at løsne spændte muskler før eller efter træning, reducere ophobning af mælkesyre og affaldsstoffer, forebygge overbelastningsskader og forkorte restitutionstiden markant.</p>
      <p>Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> arbejder Tine med over 24 års solid erfaring og stor faglig ballast. Tine er <strong>RAB-godkendt massør</strong> (Registreret Alternativ Behandler), hvilket er det <strong>eneste officielle kvalitetsstempel</strong> for massører i Danmark og dét, som udløser, at sundhedsforsikringer dækker behandlingen. Fysiurgisk massage hos Tine er godkendt ved <strong>Falck Healthcare</strong>, og massage dækkes ligeledes ved <strong>AP Pensions sundhedsforsikring</strong>.</p>
      <p><em>Husk venligst altid selv at medbringe lagen og håndklæde til din behandling.</em></p>
    `,
    benefits: [
      { title: 'RAB-godkendt massør', desc: 'Danmarks eneste officielle kvalitetsstempel for massører. Udløser dækning fra bl.a. Falck Healthcare og AP Pension.' },
      { title: 'Sportsmassage til atleter og motionister', desc: 'Forebygger skader, fjerner muskelømhed og optimerer din restitution.' },
      { title: 'Løsning af dybe muskelspændinger', desc: 'Hjælper med at afspænde muskler og sener samt reducere myoser og stivhed.' },
      { title: 'Øget blodcirkulation', desc: 'Forbedrer ilt- og næringsstoftilførslen og hjælper med at udskille affaldsstoffer.' },
      { title: 'Smertelindring i led', desc: 'Ledsmerter lindres, når muskler og sener slipper deres stramme spændingstræk i leddene.' },
      { title: 'Forbedret mobilitet', desc: 'Løsner muskler og sener, hvilket giver bedre bevægelighed, da det ikke længere trækker i leddene.' }
    ],
    durations: [
      { time: '30 minutter', desc: 'Målrettet behandling af et specifikt område, fx nakke, skuldre eller lænd.' },
      { time: '45 minutter', desc: 'Fokuseret fysiurgisk massage eller sportsmassage af ryg og ben.' },
      { time: '60 minutter', desc: 'En grundig, dybere helkrops- eller ryg-/nakkemassage med god tid.' },
      { time: '90 minutter', desc: 'Komplet helkropsmassage med god tid til fordybelse og problempunkter.' }
    ]
  },
  'zoneterapi': {
    title: 'Zoneterapi (voksne & babyer)',
    therapist: 'Sophie Hebsgaard Ede (RAB-godkendt zoneterapeut)',
    price_info: 'Fra 600 kr. (RAB-godkendt) · Tilskud fra ”danmark” og sundhedsforsikringer',
    gallery_slug: 'zoneterapi',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/zoneterapi-fodmassage-reflekspunkter.jpeg'],
    short_description: 'Naturlig og nænsom reflekszonebehandling for voksne og babyer. Tilskud fra Sygeforsikringen ”danmark” og sundhedsforsikringer.',
    content: `
      <p>Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> tilbyder vi professionel og RAB-godkendt zoneterapi i rolige, hyggelige omgivelser i Ryomgård. Zoneterapi er en helhedsorienteret behandlingsform, hvor man gennem målrettet reflekszonebehandling under og på fødderne stimulerer kroppens egne selvhelbredende kræfter og genskaber naturlig balance.</p>
      <p><strong>Zoneterapi til babyer:</strong> Sophie udfører desuden blid og nænsom zoneterapi til spædbørn og babyer, der er plaget af ondt i maven, kolik, søvnbesvær eller uro.</p>
      <p><em>Husk venligst selv at medbringe lagen og gerne et stort håndklæde til zoneterapien.</em></p>
      
      <h2>Hvordan virker zoneterapi og reflekszoner?</h2>
      <p>Hvert organ, muskelgruppe, nervebane og kirtel i kroppen har tilsvarende reflekszoner under og på oversiden af fødderne. Når der opstår ubalancer, spændinger eller blokeringer i kroppen, vil de tilhørende zoner på fødderne ofte føles ømme eller spændte. Ved blid og præcis stimulering af disse punkter fremmes blodcirkulationen, lymfedrænage stimuleres, og kroppens nervesystem beroliges.</p>
      
      <h2>Hvem har gavn af zoneterapi?</h2>
      <p>Zoneterapi er velegnet til både voksne, gravide, børn og spædbørn. Behandlingen anvendes hyppigt mod en lang række problemstillinger, herunder:</p>
      <ul>
        <li>Fordøjelsesproblemer, mavesmerter og oppustethed</li>
        <li>Hovedpine, migræne og spændinger i nakke og skuldre</li>
        <li>Hormonelle ubalancer og menstruationssmerter</li>
        <li>Stress, uro, søvnbesvær og udmattethed</li>
        <li>Styrkelse af immunforsvaret og restitution efter sygdom</li>
        <li>Kolik og mavekneb hos spædbørn</li>
      </ul>
      <p>Sophie Hebsgaard Ede er RAB-godkendt zoneterapeut, hvilket betyder, at mange private sundhedsforsikringer samt Sygeforsikringen ”danmark” yder tilskud til behandlingen.</p>

      <h2>Kombinér zoneterapi med voksbehandling og kropspleje</h2>
      <p>Ønsker du en komplet forkælelse af kroppen, kan du med fordel kombinere din zoneterapi med en professionel <a href="/behandlinger/voksbehandling" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">voksbehandling</a> hos vores kosmetolog Dianna. Mens zoneterapien skaber ro og balance indefra, efterlader voksbehandling huden silkeblød og glat i ugevis.</p>

      <h2>Din zoneterapi på Djursland starter i Ryomgård</h2>
      <p>Klinikken ligger på Poppelvej 52 i Ryomgård. Vi byder klienter velkommen fra hele Djursland. Læs mere <a href="/om-os">om os</a> eller se vores <a href="/prisliste">prisliste</a>, og tag endelig <a href="/kontakt">kontakt til klinikken</a> ved spørgsmål.</p>
    `,
    benefits: [
      { title: 'RAB-godkendt behandler', desc: 'Garanti for faglig uddannelse og mulighed for tilskud fra Sygeforsikringen ”danmark”.' },
      { title: 'Naturlig smertelindring', desc: 'Dæmper hovedpine, muskelspændinger og mavegener gennem føddernes reflekszoner.' },
      { title: 'Stressreduktion', desc: 'Sænker stressniveauet og hjælper kroppen tilbage i en dyb, regenererende hviletilstand.' },
      { title: 'Styrket immunforsvar', desc: 'Aktiverer kredsløb og udskillelse af affaldsstoffer for fornyet energi.' }
    ],
    durations: [
      { time: '75 minutter', desc: 'Første konsultation med grundig journalsamtale og behandling.' },
      { time: '60 minutter', desc: 'Efterfølgende behandlinger med fokus på føddernes reflekszoner.' }
    ]
  },
  'ems-traening': {
    title: 'EMS dragt (KUN dragt, ingen træning)',
    therapist: 'Djurs Massage Team',
    price_info: '300 kr. for 30 minutter',
    gallery_slug: 'ems-traening',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/ems-traening-dragt-klinik.jpg'],
    short_description: 'KUN dragt, ingen træning! 300 kr. for 30 minutter inkl. af- og påklædning samt rengøring. Vi tilbyder ikke gratis prøvetræning.',
    content: `
      <p>Hos Djurs Massage benytter vi en avanceret EMS dragt (Elektro-Myo-Stimulation). Vigtigt: <strong>Hos os er det KUN dragt – absolut ingen hård træning!</strong></p>
      <p>EMS-dragten koster <strong>300 kr.</strong> Sessionen varer <strong>30 minutter</strong>, da der er afsat god tid til, at du kan komme i og ud af dragten, samt til omhyggelig og hygiejnisk rengøring. Bemærk venligst, at <strong>vi ikke tilbyder gratis prøvetræning</strong>.</p>
      <p>EMS-dragten kan anvendes <strong>alene for målrettet at styrke musklerne</strong>, eller i en <strong>effektiv kombination med massage</strong>.</p>
      <p>Milde elektriske impulser stimulerer muskulaturen dybt og effektivt helt uden mekanisk vægt- eller led-belastning. Herudover er EMS-dragten enormt effektiv til smertelindring: Den aktiverer kroppens egne smertehæmmende stoffer, samtidig med at muskler og sener slipper deres spænding i leddene. Det giver en markant lettelse for dig med spændinger eller stivhed.</p>
      <p><em>Husk venligst selv at medbringe lagen og håndklæde.</em></p>
    `,
    benefits: [
      { title: 'KUN dragt – ingen træning', desc: 'Du skal ikke svede eller lave hårde øvelser. Dragten aktiverer musklerne skånsomt.' },
      { title: 'Styrker muskler skånsomt', desc: 'Opbygger muskelstyrke helt uden led- og seneslitage.' },
      { title: 'Aktiverer smertehæmmende stoffer', desc: 'Kroppens egne smertehæmmende stoffer frigives for effektiv lindring.' },
      { title: 'Aflaster leddene', desc: 'Ledsmerter dæmpes, når muskler og sener slipper deres træk og spænding.' },
      { title: 'Alene eller med massage', desc: 'Vælg dragten som selvstændig behandling eller som supplement til massage.' }
    ],
    durations: [
      { time: '30 minutter (300 kr.)', desc: 'Inkl. af- og påklædning af dragt, ca. 20 min. stimulation samt hygiejnisk rengøring.' },
      { time: 'Kombination med massage', desc: 'Kombiner EMS dragten med dybdegående fysiurgisk massage for maksimal effekt.' }
    ]
  },
  'ems-dragter-traening': {
    title: 'EMS dragt (KUN dragt, ingen træning)',
    therapist: 'Djurs Massage Team',
    price_info: '300 kr. for 30 minutter',
    gallery_slug: 'ems-traening',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/ems-traening-dragt-klinik.jpg'],
    short_description: 'KUN dragt, ingen træning! 300 kr. for 30 minutter inkl. af- og påklædning samt rengøring. Vi tilbyder ikke gratis prøvetræning.',
    content: `
      <p>Hos Djurs Massage benytter vi en avanceret EMS dragt (Elektro-Myo-Stimulation). Vigtigt: <strong>Hos os er det KUN dragt – absolut ingen hård træning!</strong></p>
      <p>EMS-dragten koster <strong>300 kr.</strong> Sessionen varer <strong>30 minutter</strong>, da der er afsat god tid til, at du kan komme i og ud af dragten, samt til omhyggelig og hygiejnisk rengøring. Bemærk venligst, at <strong>vi ikke tilbyder gratis prøvetræning</strong>.</p>
      <p>EMS-dragten kan anvendes <strong>alene for målrettet at styrke musklerne</strong>, eller i en <strong>effektiv kombination med massage</strong>.</p>
      <p>Milde elektriske impulser stimulerer muskulaturen dybt og effektivt helt uden mekanisk vægt- eller led-belastning. Herudover er EMS-dragten enormt effektiv til smertelindring: Den aktiverer kroppens egne smertehæmmende stoffer, samtidig med at muskler og sener slipper deres spænding i leddene. Det giver en markant lettelse for dig med spændinger eller stivhed.</p>
      <p><em>Husk venligst selv at medbringe lagen og håndklæde.</em></p>
    `,
    benefits: [
      { title: 'KUN dragt – ingen træning', desc: 'Du skal ikke svede eller lave hårde øvelser. Dragten aktiverer musklerne skånsomt.' },
      { title: 'Styrker muskler skånsomt', desc: 'Opbygger muskelstyrke helt uden led- og seneslitage.' },
      { title: 'Aktiverer smertehæmmende stoffer', desc: 'Kroppens egne smertehæmmende stoffer frigives for effektiv lindring.' },
      { title: 'Aflaster leddene', desc: 'Ledsmerter dæmpes, når muskler og sener slipper deres træk og spænding.' },
      { title: 'Alene eller med massage', desc: 'Vælg dragten som selvstændig behandling eller som supplement til massage.' }
    ],
    durations: [
      { time: '30 minutter (300 kr.)', desc: 'Inkl. af- og påklædning af dragt, ca. 20 min. stimulation samt hygiejnisk rengøring.' },
      { time: 'Kombination med massage', desc: 'Kombiner EMS dragten med dybdegående fysiurgisk massage for maksimal effekt.' }
    ]
  },
  'relax-massage': {
    title: 'Relax massage',
    therapist: 'Diana (Uddannet kosmetolog)',
    price_info: '600 kr. per time (30 min ryg / 60 min helkrop)',
    gallery_slug: 'relax-massage',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/relax-massage-afslappende-rygmassage.png'],
    short_description: 'Rolig og afstressende wellness-massage med fokus på afspænding, nærvær og forkælelse.',
    content: `
      <p>Har du brug for at koble fra og give kroppen en velfortjent pause? Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> er Relax Massage en rolig og afslappende massage med fokus på velvære, ro og afspænding.</p>
      <p>I modsætning til vores mere dybdegående fysiurgiske massage er formålet her først og fremmest, at du får mulighed for at slippe tankerne, slappe af og nyde behandlingen i et lunt og behageligt miljø.</p>
      <p>Behandlingen udføres af Diana, der er uddannet kosmetolog i Spanien og har stor erfaring med beroligende massageteknikker og hudpleje.</p>
    `,
    benefits: [
      { title: 'Total afstresning', desc: 'Sænker puls og blodtryk og giver et tiltrængt pusterum fra hverdagen.' },
      { title: 'Blid muskelafspænding', desc: 'Løsner overfladiske spændinger uden hårdt eller smertefuldt tryk.' },
      { title: 'Styrket velvære', desc: 'Frigiver kroppens egne feel-good signalstoffer.' },
      { title: 'Mulighed for tilkøb', desc: 'Kombinér med ekstra hovedbunds- og ansigtsmassage for maksimal nydelse.' }
    ],
    durations: [
      { time: '30 minutter', desc: 'Fokus på ryg, nakke og skuldre – ideel som pause i en travl hverdag.' },
      { time: '60 minutter', desc: 'Rolig helkropsmassage, hvor der er rigelig tid til at komme helt ned i tempo.' }
    ]
  },
  'voksbehandling': {
    title: 'Voksbehandling & Threading',
    therapist: 'Dianna (Kosmetolog)',
    price_info: 'Fra 300 kr. afhængig af behandlingsområde (tjek Gecko booking)',
    gallery_slug: 'voksbehandling',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/voksbehandling-klinik.jpg'],
    short_description: 'Professionel voksbehandling og threading i Ryomgård udført med den rette voks af uddannet kosmetolog. Skånsom hårfjerning og langvarigt resultat.',
    content: `
      <p>Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> tilbyder vores uddannede kosmetolog Dianna professionel hårfjerning og voksbehandling i rolige rammer i Ryomgård.</p>

      <h2>Hårfjerning udført med den rette voks</h2>
      <p>Behandlingen udføres altid med den helt rette, professionelle voks til det specifikke område, så huden skånes mest muligt og efterlades blød og velplejet. Du behøver ikke at bekymre dig om særlige forberedelser eller restriktioner forud for behandlingen – al relevant vejledning om pleje gives direkte og personligt i klinikken.</p>

      <h2>Behandlingsområder</h2>
      <p>Vi tilbyder voksbehandling og hårfjerning på følgende områder:</p>
      <ul>
        <li><strong>Bikini:</strong> Skånsom voksning af bikinilinjen.</li>
        <li><strong>Ben & arme:</strong> Underben, hele ben eller arme med langvarig glathed.</li>
        <li><strong>Ryg & bryst:</strong> Effektiv hårfjerning til mænd og kvinder.</li>
        <li><strong>Bryn & overlæbe:</strong> Udføres med <em>threading (trådning)</em> for et præcist, skarpt og yderst skånsomt resultat uden voks.</li>
      </ul>

      <h2>Medbring altid eget lagen og håndklæde</h2>
      <p>Der anvendes ikke engangsmateriale i klinikken. Klienter skal derfor fortsat have <strong>eget lagen og håndklæde med</strong> til behandlingen – præcis ligesom til alle klinikkens øvrige ydelser.</p>

      <h2>Betaling</h2>
      <p>Efter behandlingen kan du betale med <strong>kortbetaling, MobilePay eller kontant</strong>.</p>
    `,
    benefits: [
      { title: 'Den rette voks', desc: 'Udføres altid med skånsom kvalitetsvoks afstemt efter hudområdet.' },
      { title: 'Threading af bryn & overlæbe', desc: 'Præcis trådning til ansigtet – skånsomt og veldefineret.' },
      { title: 'Bikini voks', desc: 'Hurtig og professionel voksning af bikinilinjen.' },
      { title: 'Uddannet kosmetolog', desc: 'Dianna er uddannet i Spanien og sikrer en tryg og omhyggelig behandling.' }
    ],
    durations: [
      { time: '15-45 minutter', desc: 'Afhænger af området (fx threading, bikini, ben eller ryg).' }
    ]
  },
  'pedicure-fodbehandling': {
    title: 'Pedicure & Fodbehandling',
    therapist: 'Dianna (Uddannet kosmetolog)',
    price_info: 'Fra 300 kr.',
    gallery_slug: 'pedicure-fodbehandling',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/pedicure-fodmassage-zoneterapi-behandling.jpeg'],
    short_description: 'Omsorgsfuld og skånsom pedicure og fodpleje for sunde, bløde og velplejede fødder. Hård hud slibes/files skånsomt (beskæres ikke).',
    content: `
      <p>Velplejede fødder giver øget velvære i hele kroppen. Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> tilbyder vores uddannede kosmetolog Dianna skånsom og grundig pedicure og fodbehandling.</p>
      <p>Hård hud fjernes nænsomt ved at slibe og file – den <strong>beskæres ikke</strong>, da slibning/filing er langt mere skånsomt for huden. Neglene klippes og formes, neglebånd plejes, og behandlingen afsluttes med en let fodmassage og nærende creme.</p>
      <p>Behandlingen foregår i vores hyggelige, rolige omgivelser på Poppelvej i Ryomgård.</p>
      <p><em>Husk venligst altid selv at medbringe lagen og håndklæde til behandlingen. Der anvendes ikke engangsmaterialer.</em></p>
    `,
    benefits: [
      { title: 'Skånsom fjernelse af hård hud', desc: 'Hård hud slibes og files nænsomt – beskæres ikke, hvilket er mere skånsomt.' },
      { title: 'Negle- og neglebåndspleje', desc: 'Formning, klipning og grundig pleje for sunde og pæne negle.' },
      { title: 'Afslappende fodmassage', desc: 'Fremmer cirkulationen og løsner spændinger i fødder og ankler.' },
      { title: 'Hyggelige omgivelser', desc: 'Nyd behandlingen i klinikkens behagelige, hjemlige rammer i Ryomgård.' }
    ],
    durations: [
      { time: '45-60 minutter', desc: 'Fodbad, peeling, skånsom slibning/filing af hård hud, neglepleje og afsluttende creme.' }
    ]
  },
  'cupping-massage': {
    title: 'Cupping massage',
    therapist: 'Tine (RAB-godkendt massør)',
    price_info: 'Fra 300 kr. (600 kr./time)',
    gallery_slug: 'cupping-massage',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/cupping-massage-vakuum-behandling.jpg'],
    short_description: 'Vakuumkopper skaber undertryk til stimulering af bindevæv og blodcirkulation. Giver ikke store blå mærker – god effekt ved cellulitis/appelsinhud.',
    content: `
      <p>Cuppingmassage er en behandlingsform, hvor vakuumkopper anvendes til at skabe undertryk på huden for at øge blodcirkulationen, løsne spændinger og stimulere kroppens naturlige helingsproces.</p>
      <p>Hos <a href="/" class="font-semibold text-[#1E3A5F] hover:text-[#C9A770] underline decoration-[#C9A770]">Djurs Massage</a> tilpasses cupping-teknikken omhyggeligt, så den <strong>ikke giver store blå mærker</strong>. Cupping er desuden kendt for sin <strong>gode effekt ved cellulitis / appelsinhud</strong>, da det løsner stramt bindevæv og sætter skub i vævets mikrocirkulation.</p>
      <p><em>Husk venligst selv at medbringe lagen og håndklæde.</em></p>
    `,
    benefits: [
      { title: 'Uden store blå mærker', desc: 'Tilpasses præcist så vævet stimuleres uden unødige misfarvninger.' },
      { title: 'Effekt mod cellulitis / appelsinhud', desc: 'Løsner bindevævet og øger blodgennemstrømningen for en glattere hud.' },
      { title: 'Dyb bindevævsafspænding', desc: 'Løfter vævet og skaber plads til forbedret mikrocirkulation.' },
      { title: 'Udskillelse af affaldsstoffer', desc: 'Hjælper lymfesystemet med at transportere ophobede stoffer bort.' }
    ],
    durations: [
      { time: '30-60 minutter', desc: 'Indgår naturligt som del af eller fuld fysiurgisk behandling.' }
    ]
  },
  'specialiserede-behandlinger': {
    title: 'Specialiserede teknikker',
    therapist: 'Tine (RAB-godkendt massør)',
    price_info: 'Fra 300 kr. (600 kr./time) · RAB-godkendt',
    gallery_slug: 'special-behandlinger',
    image: ['https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/ultralyd-laser-behandling-muskler.jpg'],
    short_description: 'Målrettede teknikker: Triggerpunktsmassage OG nåle, laser/ultralyd, scraping, dry needling og 5-punkts øreakupunktur.',
    content: `
      <p>Hos Djurs Massage kombinerer vi klassisk massage med moderne og målrettede specialteknikker for at opnå det hurtigste og mest holdbare resultat for dig:</p>
      <ul>
        <li><strong>Triggerpunktsmassage OG nåle:</strong> Målrettet tryk på specifikke spændingsknuder og præcis brug af nåle for at fjerne udstrålende smerter.</li>
        <li><strong>Scraping (Gua Sha):</strong> Løsner bindevæv og øger blodcirkulationen markant – ligesom gua sha, som mange kender fra ansigtsbehandling/ansigtsløft.</li>
        <li><strong>Dry needling:</strong> Løsner muskelspændinger bl.a. ved hjælp af nervestimuli i musklen med tynde akupunkturnåle.</li>
        <li><strong>Cupping massage:</strong> Uden store blå mærker – god effekt på bindevæv, blodcirkulation og cellulitis/appelsinhud.</li>
        <li><strong>Laser- og ultralydsbehandling:</strong> Ikke-invasiv teknik der fremmer heling og reducerer inflammation ved sene- og muskelskader.</li>
        <li><strong>5-punkts øreakupunktur:</strong> Anvendes til stresshåndtering, smertelindring, søvnproblemer og mental balance.</li>
        <li><strong>Kinesiotape:</strong> Støtter led og muskler i hverdagen og ved sport uden at nedsætte bevægeligheden.</li>
      </ul>
      <p><em>Husk venligst selv at medbringe lagen og håndklæde.</em></p>
    `,
    benefits: [
      { title: 'Skræddersyet kombination', desc: 'Vi vurderer din krop og vælger netop den teknik, der virker bedst for dig.' },
      { title: 'Hurtig smertelindring', desc: 'Målrettet indsats ved akutte og kroniske muskel- og senespændinger.' },
      { title: 'Fuld faglighed', desc: 'Udføres af Tine Vium Thorsen med første uddannelse fra 2001 og RAB-godkendelse.' }
    ],
    durations: [
      { time: '30-90 minutter', desc: 'Indgår i din normale behandlingstid (priser fra 300 kr. / 600 kr. i timen).' }
    ]
  }
};

// Helper to parse rich-text HTML into structured sections by <h2> tags
function parseRichContent(html) {
  if (!html) return [];
  if (typeof window === 'undefined' || !window.DOMParser) {
    return [{ title: '', html, isIntro: true }];
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Mærk lister med kun ultrakorte ord/tags (som f.eks. "Ben", "Arme") med area-chips
    doc.querySelectorAll('ul').forEach((ul) => {
      const lis = Array.from(ul.querySelectorAll('li'));
      const allShort = lis.length > 0 && lis.every((li) => li.textContent.trim().length <= 20);
      if (allShort) {
        ul.classList.add('area-chips');
      }
    });

    // Hvis et li starter med <strong>, sikres det at overskriften vises som blok med teksten under
    doc.querySelectorAll('ul li').forEach((li) => {
      const strong = li.querySelector(':scope > strong:first-child');
      if (strong) {
        strong.classList.add('block', 'text-[#1E3A5F]', 'font-bold', 'mb-1');
      }
    });

    const nodes = Array.from(doc.body.childNodes);
    const sections = [];
    let currentSec = { title: '', html: '', isIntro: true };

    for (const node of nodes) {
      if (node.nodeName && node.nodeName.toLowerCase() === 'h2') {
        if (currentSec.html.trim() || currentSec.title) {
          sections.push(currentSec);
        }
        currentSec = {
          title: node.textContent.trim(),
          html: '',
          isIntro: false
        };
      } else {
        const outer = node.nodeType === 1 ? node.outerHTML : (node.textContent || '');
        currentSec.html += outer;
      }
    }
    if (currentSec.html.trim() || currentSec.title) {
      sections.push(currentSec);
    }
    return sections;
  } catch {
    return [{ title: '', html, isIntro: true }];
  }
}

const getSectionMeta = (title) => {
  const t = (title || '').toLowerCase();
  if (t.includes('hvad er') || t.includes('forvente')) {
    return {
      icon: 'fa-solid fa-spa',
      badge: 'Forstå behandlingen',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('hvordan virker') || t.includes('hårfjerning med voks')) {
    return {
      icon: 'fa-solid fa-wand-magic-sparkles',
      badge: 'Sådan virker det',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('ben og andre områder') || t.includes('områder')) {
    return {
      icon: 'fa-solid fa-list-check',
      badge: 'Behandlingsområder',
      accentColor: 'border-l-[#C9A770]'
    };
  }
  if (t.includes('hvor længe holder') || t.includes('holder en voksbehandling')) {
    return {
      icon: 'fa-solid fa-clock-rotate-left',
      badge: 'Holdbarhed & Resultat',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('gør huden klar') || t.includes('klar til voks') || t.includes('forberedelse')) {
    return {
      icon: 'fa-solid fa-circle-check',
      badge: 'Før behandlingen',
      accentColor: 'border-l-[#C9A770]'
    };
  }
  if (t.includes('giv huden ro') || t.includes('ro efter') || t.includes('efterbehandling')) {
    return {
      icon: 'fa-solid fa-heart-pulse',
      badge: 'Efterbehandling & Pleje',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('hvorfor') || t.includes('zoner') || t.includes('refleks')) {
    return {
      icon: 'fa-solid fa-shoe-prints',
      badge: 'Reflekszoner på fødderne',
      accentColor: 'border-l-[#C9A770]'
    };
  }
  if (t.includes('sådan foregår') || t.includes('behandling')) {
    return {
      icon: 'fa-solid fa-clipboard-check',
      badge: 'Forløb & Varighed',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('samtale') || t.includes('helhed') || t.includes('hensyn')) {
    return {
      icon: 'fa-solid fa-heart-pulse',
      badge: 'Tryghed & Hensyn',
      accentColor: 'border-l-[#C9A770]'
    };
  }
  if (t.includes('ryomgård') || t.includes('djursland') || t.includes('base') || t.includes('klinik') || t.includes('adresse')) {
    return {
      icon: 'fa-solid fa-location-dot',
      badge: 'Klinikken i Ryomgård',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  if (t.includes('pris') || t.includes('tilskud')) {
    return {
      icon: 'fa-solid fa-hand-holding-dollar',
      badge: 'Priser & Forsikring',
      accentColor: 'border-l-[#C9A770]'
    };
  }
  if (t.includes('bestil') || t.includes('booking') || t.includes('tid')) {
    return {
      icon: 'fa-solid fa-calendar-check',
      badge: 'Tidsbestilling',
      accentColor: 'border-l-[#1E3A5F]'
    };
  }
  return {
    icon: 'fa-solid fa-circle-info',
    badge: 'Information',
    accentColor: 'border-l-[#C9A770]'
  };
};

export default function TreatmentDetailPage() {
  const { slug: routeSlug } = useParams();
  const location = useLocation();
  const slug = routeSlug || (location.pathname.includes('zoneterapi') ? 'zoneterapi' : location.pathname.includes('voksbehandling') ? 'voksbehandling' : 'fysiurgisk-massage');
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);

  const bookingUrl =
    'https://djursmassage.app4.geckobooking.dk/site/booking.php?show=new_booking&icCode=69a506a6f06a0ca7022b880e8f6bcb65b11332&bId=11332&aDIfH=1&dTpl=1&sSToken=267bea05a53684a4a7d7ad2e6bae564a';

  useEffect(() => {
    setLoading(true);
    // Fetch from CMS if available
    fetch('/_api/public/v2/content/behandlinger')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const found = data?.items?.find(
          (item) =>
            item.slug === slug ||
            item.slug?._ === slug ||
            item.galleri_reference === slug ||
            (slug === 'fysiurgisk-massage' && (item.titel || item.title)?.toLowerCase().includes('fysiurgisk')) ||
            (slug === 'zoneterapi' && (item.titel || item.title)?.toLowerCase().includes('zoneterapi')) ||
            (slug === 'relax-massage' && (item.titel || item.title)?.toLowerCase().includes('relax')) ||
            (slug === 'voksbehandling' && (item.titel || item.title)?.toLowerCase().includes('voks')) ||
            (slug === 'pedicure-fodbehandling' && (item.titel || item.title)?.toLowerCase().includes('pedicure')) ||
            (slug === 'cupping-massage' && (item.titel || item.title)?.toLowerCase().includes('cupping')) ||
            (slug?.includes('ems') && (item.titel || item.title)?.toLowerCase().includes('ems')) ||
            (slug === 'specialiserede-behandlinger' && (item.titel || item.title)?.toLowerCase().includes('special'))
        );

        if (found) {
          setTreatment({
            ...(FALLBACK_TREATMENTS[slug] || {}),
            ...found,
            title: found.titel || found.title || FALLBACK_TREATMENTS[slug]?.title,
            therapist: found.behandler || found.therapist || FALLBACK_TREATMENTS[slug]?.therapist,
            price_info: found.pris_og_tid || found.price_info || FALLBACK_TREATMENTS[slug]?.price_info,
            short_description: found.kort_beskrivelse || found.short_description || FALLBACK_TREATMENTS[slug]?.short_description,
            content: found.indhold || found.content || FALLBACK_TREATMENTS[slug]?.content,
            gallery_slug: found.galleri_reference || found.gallery_slug || slug,
            image: found.billede || found.image || FALLBACK_TREATMENTS[slug]?.image,
          });
        } else {
          setTreatment(FALLBACK_TREATMENTS[slug] || null);
        }
      })
      .catch(() => {
        setTreatment(FALLBACK_TREATMENTS[slug] || null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const current = treatment || FALLBACK_TREATMENTS[slug];

  if (loading && !current) {
    return (
      <div className="py-24 text-center text-slate-500">
        <i className="fa-solid fa-circle-notch fa-spin text-2xl mb-3 text-[#1E3A5F]" aria-hidden="true" />
        <p className="text-sm">Henter behandlingsoplysninger...</p>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] font-serif mb-4">
          Behandling ikke fundet
        </h1>
        <p className="text-slate-600 mb-6">
          Den ønskede behandling kunne ikke findes. Se vores fulde oversigt over behandlinger nedenfor.
        </p>
        <Link
          to="/behandlinger"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1E3A5F] text-white text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
        >
          <span>Se alle behandlinger</span>
        </Link>
      </div>
    );
  }

  const mainImage = Array.isArray(current.image) ? current.image[0] : current.image;

  return (
    <>
      <SEOHead
        title={`${current.title} | Djurs Massage`}
        description={current.short_description || `${current.title} hos Djurs Massage i Ryomgård. Professionel og grundig behandling.`}
        ogImage={mainImage}
      />

      {/* Page Header - Clean, follows container line, no box wrapper */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        {/* Breadcrumb navigation */}
        <nav className="flex items-center gap-2 text-xs text-slate-500 mb-4" aria-label="Brødkrummer">
          <Link to="/" className="hover:text-[#1E3A5F] transition-colors">Startside</Link>
          <span>/</span>
          <Link to="/behandlinger" className="hover:text-[#1E3A5F] transition-colors">Behandlinger</Link>
          <span>/</span>
          <span className="text-[#1E3A5F] font-medium">{current.title}</span>
        </nav>

        <div className="max-w-3xl">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1E3A5F] font-serif leading-tight">
            {current.title}
          </h1>

          {current.therapist && (
            <p className="text-xs sm:text-sm font-semibold text-[#1E3A5F]/80 mt-1">
              Behandler: {current.therapist}
            </p>
          )}

          {current.short_description && (
            <p className="text-slate-700 text-sm sm:text-base mt-3 leading-relaxed">
              {current.short_description}
            </p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
            >
              <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
              <span>Book tid nu</span>
            </a>
            <Link
              to="/prisliste"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-transparent hover:bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors"
            >
              <span>Se prisliste & klippekort</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left Column: In-depth Content */}
          <div className="lg:col-span-7 space-y-6">
            {/* Synlig boks: Sundhedsforsikring / Falck / "danmark" tilskud eller Kosmetologisk Voksbehandling */}
            {(slug === 'fysiurgisk-massage' || slug === 'zoneterapi' || slug === 'voksbehandling') && (
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FAF8F5] via-[#F5EFE6] to-[#EFE7D8] border border-[#C9A770]/40 p-6 sm:p-8 shadow-sm">
                {/* Dekorativ diskret vandmærke-ikon i baggrunden */}
                <div className="absolute -right-6 -bottom-8 text-[#C9A770]/10 text-9xl sm:text-[11rem] pointer-events-none select-none" aria-hidden="true">
                  <i className={slug === 'voksbehandling' ? 'fa-solid fa-sparkles' : 'fa-solid fa-shield-heart'} />
                </div>

                <div className="relative z-10 space-y-6">
                  {/* Header med badge & titel */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-[#C9A770]/25">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] text-[#C9A770] flex items-center justify-center shrink-0 text-xl shadow-xs ring-4 ring-[#C9A770]/20">
                        <i className={slug === 'voksbehandling' ? 'fa-solid fa-wand-magic-sparkles' : 'fa-solid fa-shield-heart'} aria-hidden="true" />
                      </div>
                      <div>
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#C9A770]">
                          <i className="fa-solid fa-award text-[10px]" aria-hidden="true" />
                          <span>{slug === 'voksbehandling' ? 'Uddannet Kosmetolog & Hygiejne' : 'Sundhedsforsikring & Tilskud'}</span>
                        </span>
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
                          {slug === 'voksbehandling' ? 'Professionel voksbehandling med fokus på din hud' : 'Tjek om din behandling er dækket'}
                        </h2>
                      </div>
                    </div>

                    {slug === 'voksbehandling' ? (
                      <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] text-[#F3E8D2] text-xs font-semibold rounded-full shadow-2xs">
                        <i className="fa-solid fa-user-graduate text-[#C9A770]" aria-hidden="true" />
                        <span>Uddannet i Spanien</span>
                      </span>
                    ) : slug === 'zoneterapi' ? (
                      <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] text-[#F3E8D2] text-xs font-semibold rounded-full shadow-2xs">
                        <i className="fa-solid fa-certificate text-[#C9A770]" aria-hidden="true" />
                        <span>RAB-godkendt</span>
                      </span>
                    ) : (
                      <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] text-[#F3E8D2] text-xs font-semibold rounded-full shadow-2xs">
                        <i className="fa-solid fa-hospital-user text-[#C9A770]" aria-hidden="true" />
                        <span>Falck Healthcare</span>
                      </span>
                    )}
                  </div>

                  {slug === 'voksbehandling' ? (
                    <>
                      {/* Overskuelige nøgle-kort for voksbehandling præcis som zoneterapi */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                        {/* Kort 1: Kosmetolog */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-user-doctor" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Uddannet Kosmetolog</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Udføres af vores kosmetolog, der er uddannet i Spanien. Stor omhu for formning af bryn og din specifikke hudtype.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-check text-[10px]" aria-hidden="true" /> Tryghed & faglig ekspertise
                          </span>
                        </div>

                        {/* Kort 2: Hygiejne & Skånsomhed */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Hygiejne & Omsorg</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Vi anvender skånsomme kvalitetsprodukter, rene engangsmaterialer og beroligende efterbehandlingsolie til huden.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-check text-[10px]" aria-hidden="true" /> Rene engangsmaterialer
                          </span>
                        </div>

                        {/* Kort 3: Resultat & Glathed */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-sparkles" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Glat hud i ugevis</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Hårene trækkes ud med roden. Det giver et glattere resultat, og der går længere tid, før hårvæksten igen mærkes.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-calendar-days text-[10px]" aria-hidden="true" /> Slip for daglig barbering
                          </span>
                        </div>
                      </div>

                      {/* Enkel 3-trins forberedelsesguide inspireret af zoneterapi */}
                      <div className="bg-white/80 rounded-xl p-4 sm:p-5 border border-[#E8E1D5] space-y-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-[#1E3A5F] flex items-center gap-2">
                          <i className="fa-solid fa-list-check text-[#C9A770]" aria-hidden="true" />
                          <span>Sådan forbereder du dig i 3 nemme trin:</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">1</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Lad hårene gro</strong>
                              <span>Hårene bør være ca. 5 mm lange, så voksen kan få godt fat. Undgå barbering lige inden.</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">2</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Ren & rolig hud</strong>
                              <span>Undgå fede cremer/olier samme dag, samt voks på solskoldet eller irriteret hud.</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">3</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Giv besked</strong>
                              <span>Fortæl os ved tidsbestilling, hvilke områder du ønsker behandlet (ben, bryn, arme mv.).</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : slug === 'zoneterapi' ? (
                    <>
                      {/* Overskuelige nøgle-kort for zoneterapi */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                        {/* Kort 1: RAB-godkendelse */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-user-doctor" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">RAB-Godkendt</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Hos Djurs Massage er <strong className="text-slate-900">Sophie Hebsgaard Ede</strong> autoriseret RAB-zoneterapeut – din garanti for høj faglig standard.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-check text-[10px]" aria-hidden="true" /> Giver adgang til tilskud
                          </span>
                        </div>

                        {/* Kort 2: Danmark */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-notes-medical" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">”danmark” Tilskud</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Er du medlem af Sygeforsikringen ”danmark”, ydes der økonomisk tilskud til zoneterapi hos RAB-godkendt behandler.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-paper-plane text-[10px]" aria-hidden="true" /> Automatisk indberetning
                          </span>
                        </div>

                        {/* Kort 3: Sundhedsforsikringer */}
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-briefcase-medical" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Sundhedsforsikring</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Mange private og arbejdsgiverbetalte forsikringer (bl.a. Danica, Topdanmark m.fl.) dækker hele eller dele af forløbet.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-shield-halved text-[10px]" aria-hidden="true" /> Hel eller delvis dækning
                          </span>
                        </div>
                      </div>

                      {/* Enkel 3-trins guide */}
                      <div className="bg-white/80 rounded-xl p-4 sm:p-5 border border-[#E8E1D5] space-y-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-[#1E3A5F] flex items-center gap-2">
                          <i className="fa-solid fa-list-check text-[#C9A770]" aria-hidden="true" />
                          <span>Sådan gør du i 3 nemme trin:</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">1</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Tjek din dækning</strong>
                              <span>Forhør dig hos dit forsikringsselskab inden opstart.</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">2</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Book din tid</strong>
                              <span>Vælg din ønskede zoneterapi-behandling online.</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2.5 text-xs text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">3</span>
                            <div>
                              <strong className="block text-[#1E3A5F]">Giv os besked</strong>
                              <span>Oplys blot dit medlemskab ved betaling, så ordner vi resten.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Overskuelige nøgle-kort for fysiurgisk massage */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-hospital-user" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Falck Healthcare Samarbejde</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              <strong className="text-slate-900">Tine Vium Thorsen</strong> er registreret behandler ved Falck Healthcare.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-check text-[10px]" aria-hidden="true" /> Direkte samarbejde med Falck
                          </span>
                        </div>

                        <div className="bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-[#E8E1D5] shadow-2xs flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="w-8 h-8 rounded-lg bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center text-sm font-bold">
                              <i className="fa-solid fa-briefcase-medical" aria-hidden="true" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1E3A5F]">Private Sundhedsordninger</h3>
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Mange sundhedsforsikringer (bl.a. Falck, Danica, Topdanmark m.fl.) dækker hel eller delvis fysiurgisk- og sportsmassage.
                            </p>
                          </div>
                          <span className="mt-3 text-[11px] font-semibold text-[#8B6E3D] flex items-center gap-1">
                            <i className="fa-solid fa-shield-halved text-[10px]" aria-hidden="true" /> Hel eller delvis dækning
                          </span>
                        </div>
                      </div>

                      {/* Tip boks */}
                      <div className="flex items-start gap-3 text-xs text-slate-700 bg-white/90 p-4 rounded-xl border border-[#E8E1D5] shadow-2xs">
                        <span className="w-7 h-7 rounded-full bg-[#FAF6EE] text-[#C9A770] flex items-center justify-center shrink-0 text-sm font-bold">
                          💡
                        </span>
                        <div className="leading-relaxed">
                          <strong className="text-[#1E3A5F] block mb-0.5">Godt tip inden behandlingen:</strong>
                          Kontakt dit forsikringsselskab eller Falck på forhånd for at høre om dine dækningsmuligheder, krav om lægehenvisning samt afregningsprocedurer.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Rich text / Description - elegant, åben og overskuelig artikel med ikoner */}
            {(() => {
              const sections = parseRichContent(current.content || '');
              const hasMultipleSections = sections.length > 1;

              if (!hasMultipleSections) {
                return (
                  <div className="bg-white border border-[#E8E1D5] rounded-2xl p-6 sm:p-8 shadow-2xs relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1E3A5F] via-[#C9A770] to-[#1E3A5F]" />
                    <div
                      className="prose-treatment text-slate-700 text-sm sm:text-base leading-relaxed space-y-3"
                      dangerouslySetInnerHTML={{ __html: sections[0]?.html || current.content || '' }}
                    />
                  </div>
                );
              }

              const introSec = sections.find((s) => s.isIntro && s.html.trim());
              const otherSections = sections.filter((s) => !(s.isIntro && s.html.trim()));

              // Sektioner til bundblokke (kliniklokation og tidsbestilling) inspireret af forsiden
              const isBottomSec = (title) => {
                const t = (title || '').toLowerCase();
                return (
                  t.includes('ryomgård') ||
                  t.includes('djursland') ||
                  t.includes('base') ||
                  t.includes('bestil') ||
                  t.includes('booking') ||
                  t.includes('tid til zoneterapi')
                );
              };

              const contentCards = otherSections.filter((s) => !isBottomSec(s.title));
              const bottomCards = otherSections.filter((s) => isBottomSec(s.title));

              return (
                <div className="space-y-6">
                  {/* Top-intro inspireret af forsidens SEO-intro */}
                  {introSec && (
                    <div className="bg-white border border-[#E8E1D5] rounded-2xl p-6 sm:p-8 shadow-xs space-y-3.5">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A770]/20 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider rounded-full">
                        <i className="fa-solid fa-spa text-[#C9A770]" aria-hidden="true" />
                        <span>{current.title} i Ryomgård</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] font-serif leading-tight">
                        {slug === 'voksbehandling'
                          ? 'Voksbehandling i Ryomgård med fokus på din hud'
                          : current.title.includes('Ryomgård') || current.title.includes('med tid')
                          ? current.title
                          : `${current.title} med tid og ro til dig`}
                      </h2>
                      <div
                        className="prose-treatment text-slate-700 text-base sm:text-lg leading-relaxed font-normal"
                        dangerouslySetInnerHTML={{ __html: introSec.html }}
                      />
                    </div>
                  )}

                  {/* Faglige emnekort inspireret af forsidens 3-korts sektion */}
                  {contentCards.length > 0 && (
                    <div className="bg-white border border-[#E8E1D5] rounded-2xl shadow-xs overflow-hidden divide-y divide-[#E8E1D5]">
                      {contentCards.map((sec, idx) => {
                        const meta = getSectionMeta(sec.title);
                        return (
                          <div
                            key={idx}
                            className="p-6 sm:p-7 hover:bg-[#FAF6EE]/40 transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                              <div className="w-11 h-11 rounded-xl bg-[#FAF6EE] border border-[#E8E1D5] flex items-center justify-center text-[#C9A770] shrink-0 shadow-2xs">
                                <i className={`${meta.icon} text-lg`} aria-hidden="true" />
                              </div>
                              <div className="space-y-2.5 flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#8B6E3D] bg-[#FAF6EE] px-2.5 py-0.5 rounded-full border border-[#E8E1D5]/60">
                                    {meta.badge}
                                  </span>
                                </div>
                                <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif leading-snug">
                                  {sec.title}
                                </h3>
                                <div
                                  className="prose-treatment text-slate-700 text-sm sm:text-base leading-relaxed space-y-2"
                                  dangerouslySetInnerHTML={{ __html: sec.html }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Fremhævede bundblokke (klinik og booking) med forsidens gradient-toplinje og knapper */}
                  {bottomCards.map((sec, idx) => {
                    const isBooking =
                      (sec.title || '').toLowerCase().includes('bestil') ||
                      (sec.title || '').toLowerCase().includes('booking') ||
                      sec.html.includes('Kontakt os om pris og en tid');

                    return (
                      <div
                        key={idx}
                        className="bg-white border border-[#E8E1D5] rounded-2xl p-6 sm:p-8 shadow-xs relative overflow-hidden space-y-4"
                      >
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1E3A5F] via-[#C9A770] to-[#1E3A5F]" />

                        <div className="space-y-3">
                          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C9A770]">
                            <i
                              className={isBooking ? 'fa-solid fa-calendar-check' : 'fa-solid fa-location-dot'}
                              aria-hidden="true"
                            />
                            <span>
                              {isBooking
                                ? 'Tidsbestilling & Spørgsmål'
                                : 'Klinikken i Ryomgård • Poppelvej 52'}
                            </span>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
                            {sec.title}
                          </h3>
                          <div
                            className="prose-treatment text-slate-700 text-sm sm:text-base leading-relaxed space-y-2.5"
                            dangerouslySetInnerHTML={{ __html: sec.html }}
                          />
                        </div>

                        {isBooking && (
                          <div className="pt-2 flex flex-wrap items-center gap-3">
                            <a
                              href={bookingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-black transition-colors shadow-xs whitespace-nowrap"
                            >
                              <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                              <span>Bestil tid online</span>
                            </a>
                            <Link
                              to="/kontakt"
                              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors whitespace-nowrap"
                            >
                              <i className="fa-solid fa-envelope text-[#C9A770]" aria-hidden="true" />
                              <span>Kontakt klinikken</span>
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Benefits if available */}
            {current.benefits && current.benefits.length > 0 && (
              <div className="pt-4 border-t border-[#E8E1D5] space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif">
                  Gavnlige virkninger ved {current.title.toLowerCase()}
                </h2>
                <div className="space-y-2 text-xs sm:text-sm text-slate-700">
                  {current.benefits.map((b, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                      <div>
                        <strong>{b.title}:</strong> <span>{b.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Durations if available */}
            {current.durations && current.durations.length > 0 && (
              <div className="pt-4 border-t border-[#E8E1D5] space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif">
                  Varighed og forløb
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-slate-700">
                  {current.durations.map((d, idx) => (
                    <div key={idx} className="border-l-2 border-[#1E3A5F] pl-3 py-1 bg-slate-50/50">
                      <strong className="text-[#1E3A5F] block">{d.time}</strong>
                      <p className="text-slate-600 mt-0.5">{d.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Practical information - only shown if not already part of dedicated text */}
            {slug !== 'zoneterapi' && slug !== 'voksbehandling' && (
              <div className="pt-4 border-t border-[#E8E1D5] space-y-2 text-xs sm:text-sm text-slate-700">
                <h3 className="font-bold text-[#1E3A5F]">Praktisk information</h3>
                <p>
                  Behandlingen foregår i rolige og trygge omgivelser på Poppelvej 52 i Ryomgård. Vi anbefaler, at du møder op et par minutter før din tid, så du kan nå at lande roligt.
                </p>
                <p>
                  Betaling sker nemt via kort, MobilePay eller kontant efter behandlingen.
                </p>
              </div>
            )}

            {/* Call to action card for zoneterapi og voksbehandling */}
            {(slug === 'zoneterapi' || slug === 'voksbehandling') && (
              <div className="mt-8 p-6 bg-[#F8F5EE] border border-[#E8E1D5] flex flex-col sm:flex-row items-center justify-between gap-4 shadow-2xs">
                <div className="space-y-1 text-center sm:text-left">
                  <h3 className="font-serif font-bold text-base sm:text-lg text-[#1E3A5F]">
                    {slug === 'voksbehandling'
                      ? 'Klar til at bestille tid til voksbehandling?'
                      : 'Klar til at bestille tid til zoneterapi?'}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    {slug === 'voksbehandling'
                      ? 'Vores kosmetolog tager godt imod dig i klinikken på Poppelvej 52 i Ryomgård.'
                      : 'Sophie tager godt imod dig i klinikken på Poppelvej 52 i Ryomgård.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2.5 shrink-0">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
                  >
                    <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                    <span>Bestil tid online</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#1E3A5F] border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full hover:bg-slate-50 transition-colors"
                  >
                    <span>Kontakt klinikken</span>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Image, Info Box & Booking Card */}
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28 self-start">
            {mainImage && (
              <div className="aspect-4/3 overflow-hidden border border-[#E8E1D5] bg-[#E8E1D5]">
                <img
                  src={imageUrl(mainImage, 'width=800,height=600,fit=cover,quality=85,format=auto')}
                  alt={`${current.title} - Behandling hos Djurs Massage i Ryomgård`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Key Facts Box - Forbedret padding og rummelig typografi */}
            <div className="bg-white p-7 sm:p-8 border border-[#E8E1D5] shadow-sm space-y-5 rounded-sm">
              <h3 className="text-lg font-bold text-[#1E3A5F] font-serif border-b border-[#E8E1D5] pb-3">
                Hurtig information
              </h3>

              <div className="space-y-3.5 text-sm sm:text-base text-slate-700 divide-y divide-slate-100">
                <div className="flex items-center justify-between pt-3 first:pt-0">
                  <span className="text-slate-500 font-medium">Pris:</span>
                  <span className="font-bold text-[#1E3A5F] text-right">{current.price_info || 'Fra 300 kr.'}</span>
                </div>
                {current.therapist && (
                  <div className="flex items-center justify-between pt-3">
                    <span className="text-slate-500 font-medium">Behandler:</span>
                    <span className="font-semibold text-slate-800 text-right">{current.therapist}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-3">
                  <span className="text-slate-500 font-medium">Sted:</span>
                  <span className="text-slate-800 text-right">Poppelvej 52, Ryomgård</span>
                </div>
                <div className="flex items-center justify-between pt-3">
                  <span className="text-slate-500 font-medium">Betaling:</span>
                  <span className="text-slate-800 text-right">Kort, MobilePay eller kontant</span>
                </div>
                <div className="flex items-start justify-between pt-3">
                  <span className="text-slate-500 font-medium shrink-0 pr-4">Medbring:</span>
                  <span className="text-slate-800 text-right font-medium">
                    {slug === 'zoneterapi' ? 'Lagen & gerne et stort håndklæde' : 'Eget lagen & håndklæde'}
                  </span>
                </div>
              </div>

              <div className="pt-3">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1E3A5F] text-white text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
                >
                  <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                  <span>Gå til online booking</span>
                </a>
              </div>
            </div>

            {/* Highlight cross-link between zoneterapi and voksbehandling */}
            {slug === 'zoneterapi' && (
              <div className="bg-[#FAF7F2] p-5 border-l-4 border-[#C9A770] border border-[#E8E1D5] space-y-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#C9A770]">Anbefalet supplement</span>
                <h4 className="text-sm font-bold text-[#1E3A5F]">
                  Kombinér med voksbehandling
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Fuldend din kropspleje med skånsom og professionel hårfjerning udført af vores kosmetolog Dianna.
                </p>
                <Link
                  to="/behandlinger/voksbehandling"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E3A5F] hover:text-[#C9A770] pt-1 transition-colors"
                >
                  <span>Læs mere om voksbehandling</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            )}

            {slug === 'voksbehandling' && (
              <div className="bg-[#FAF7F2] p-5 border-l-4 border-[#C9A770] border border-[#E8E1D5] space-y-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#C9A770]">Anbefalet supplement</span>
                <h4 className="text-sm font-bold text-[#1E3A5F]">
                  Kombinér med RAB zoneterapi
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Få fornyet indre ro, bedre fordøjelse og naturlig balance med zoneterapi udført af Sophie Hebsgaard Ede.
                </p>
                <Link
                  to="/behandlinger/zoneterapi"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E3A5F] hover:text-[#C9A770] pt-1 transition-colors"
                >
                  <span>Læs mere om zoneterapi</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            )}

            {/* Other Treatments Navigation */}
            <div className="bg-slate-50 p-6 border border-[#E8E1D5] space-y-3">
              <h4 className="text-sm font-bold text-[#1E3A5F] font-serif">
                Andre behandlinger hos Djurs Massage
              </h4>
              <div className="space-y-1.5 text-xs sm:text-sm">
                <Link to="/behandlinger/fysiurgisk-massage" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Fysiurgisk massage
                </Link>
                <Link to="/behandlinger/zoneterapi" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Zoneterapi (RAB-godkendt)
                </Link>
                <Link to="/behandlinger/relax-massage" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Relax massage ved Diana
                </Link>
                <Link to="/behandlinger/voksbehandling" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Voksbehandling
                </Link>
                <Link to="/behandlinger/pedicure-fodbehandling" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Pedicure / Fodbehandling
                </Link>
                <Link to="/behandlinger/cupping-massage" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Cupping massage
                </Link>
                <Link to="/behandlinger/specialiserede-behandlinger" className="block text-slate-700 hover:text-[#1E3A5F] font-medium py-1">
                  → Specialiserede teknikker (laser, ultralyd mv.)
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery related to this treatment via CMS slug */}
        {current.gallery_slug && (
          <div className="mt-12">
            <TreatmentGallery
              treatmentSlug={current.gallery_slug}
              title={`Galleri: ${current.title}`}
              subtitle="Autentiske billeder fra klinikken og behandlingen i Ryomgård"
            />
          </div>
        )}

        {/* Relevant 5-question FAQ section for this treatment */}
        <div className="mt-14">
          <TreatmentFAQ treatmentSlug={slug} />
        </div>
      </div>
    </>
  );
}
